/* ============================================================
   American Threads Co - "Frequently bought together" (2026-10-28)

   The store has too few orders for Shopify's own algorithm to
   know what sells together, so this scores every product in the
   catalogue against the one on screen and shows the best 8:

   - same occasion (theme-christmas, theme-halloween...)      +5
     different occasions (Christmas page, Halloween item)     -6
   - each shopper group they share (dog lovers, couples,
     matching family, baby, her, him, funny/gag, office)      +3
   - a different product type that completes the order
     (sweater + pajama pants + ornament...)                    +3
     same product type (an alternative, not an add-on)         +1
   - ornament next to apparel (cheap add-on)                  +2
   - each shared design tag (gingerbread, santa-costume...)   +1, max 3
   - Shopify's own "related" pick                             +2
     (grows on its own as real orders come in)
   - trending / best-seller signal                            +1

   Then at most 2 per product type, so the row mixes items.
   New products are picked up automatically from their tags.
   ============================================================ */
(() => {
  const root = document.querySelector('[data-atc-fbt]');
  if (!root) return;

  const current = JSON.parse(root.querySelector('script[type="application/json"]').textContent);
  const LIMIT = Number(root.dataset.limit) || 8;

  const GROUPS = {
    dog: ['dog-lover-gift', 'dog-mom-gift', 'dog-dad-gift', 'cat-lover-gift', 'pet-portrait', 'theme-pet', 'dog-breed'],
    couple: ['couples-gift', 'his-and-hers', 'matching-couple-shirts', 'christmas-couples-shirt', 'matching-christmas-shirts'],
    family: ['matching-family-shirts', 'matching-family-pajamas', 'matching-family-jersey', 'christmas-family-shirts', 'family-gift', 'sibling-shirts'],
    baby: ['baby-first-christmas', 'new-baby-gift', 'kids-gift', 'kids-pajama-pants', 'kids-baseball-jersey'],
    her: ['gift-for-her', 'grandma-gift', 'mothers-day-gift', 'dog-mom-gift', 'womens-hoodie', 'womens-sweatshirt', 'womens-jersey'],
    him: ['gift-for-him', 'gift-for-dad', 'gift-for-grandpa', 'fathers-day-gift', 'dog-dad-gift'],
    funny: ['gag-gift', 'white-elephant-gift', 'funny-christmas-sweater', 'funny-christmas-shirt', 'funny-ornament', 'novelty-tee'],
    office: ['office-christmas-party', 'coworker-gift', 'secret-santa-gift'],
    faith: ['theme-faith', 'christian-shirt'],
    gothic: ['theme-sassy', 'gothic', 'skull-print', 'sugar-skull'],
  };
  // Tags too common to say anything about two designs being alike.
  const GENERIC = new Set([
    'all-over-print', 'customily', 'customily-text', 'customily-photo', 'custom-text', 'custom-name', 'custom-photo',
    'personalized-gift', 'personalized', 'new-arrival', 'trending', 'dtg', 'print-2d', 'stocking-stuffer',
    'christmas-ornament', 'button-down-shirt', 'crewneck-sweatshirt', 'comfort-colors', 'acrylic-ornament',
    'acrylic-custom-shaped', 'wood-custom-shaped', 'wooden-ornament', 'ceramic-ornament', 'ugly-christmas-sweater',
    'ugly-sweater-party', 'christmas-sweatshirt', 'christmas-tshirt', 'holiday-sweatshirt', 'holiday-tshirt',
  ]);

  const kind = (p) => {
    const t = p.tags;
    const type = (p.type || '').toLowerCase();
    if (type.includes('ornament')) return 'ornament';
    if (t.includes('style-ugly-sweater')) return 'sweater';
    if (t.includes('style-pajama-pants')) return 'pajama';
    if (t.includes('style-gildan-sweatshirt') || type.includes('sweatshirt')) return 'sweatshirt';
    if (type.includes('hoodie')) return 'hoodie';
    if (type.includes('hawaiian')) return 'hawaiian';
    if (type.includes('jersey')) return 'jersey';
    if (type.includes('shirt')) return 'tshirt';
    return type || 'other';
  };
  // Occasions only. theme-pet / theme-faith / theme-sassy... describe the
  // shopper, not the season, so they live in GROUPS instead.
  const OCCASIONS = ['theme-christmas', 'theme-halloween', 'theme-day-of-the-dead', 'theme-fathers-day', 'theme-breast-cancer'];
  const themes = (p) => p.tags.filter((t) => OCCASIONS.includes(t));
  const groups = (p) => Object.keys(GROUPS).filter((g) => GROUPS[g].some((t) => p.tags.includes(t)));

  const score = (a, b, relatedIds) => {
    let s = 0;
    const ta = themes(a), tb = themes(b);
    if (ta.some((t) => tb.includes(t))) s += 5;
    else if (ta.length && tb.length) s -= 6;

    const gb = groups(b);
    groups(a).forEach((g) => { if (gb.includes(g)) s += 3; });

    const ka = kind(a), kb = kind(b);
    s += ka === kb ? 1 : 3;
    if (kb === 'ornament' && ka !== 'ornament') s += 2;

    let design = 0;
    a.tags.forEach((t) => {
      if (!GENERIC.has(t) && !t.startsWith('theme-') && !t.startsWith('style-') && b.tags.includes(t)) design += 1;
    });
    s += Math.min(design, 3);

    if (relatedIds.has(b.id)) s += 2;
    if (b.tags.includes('trending') || b.tags.includes('best-seller')) s += 1;
    return s;
  };

  const money = (cents) => {
    const currency = (window.Shopify && Shopify.currency && Shopify.currency.active) || root.dataset.currency || 'USD';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency }).format(cents / 100);
    } catch (e) {
      return '$' + (cents / 100).toFixed(2);
    }
  };

  const card = (p) => {
    const img = p.featured_image ? p.featured_image.replace(/^\/\//, 'https://') : '';
    const sale = p.compare_at_price && p.compare_at_price > p.price;
    return `
      <a class="atc-fbt__card" href="${p.url}">
        <span class="atc-fbt__img">${img ? `<img src="${img}${img.includes('?') ? '&' : '?'}width=500" alt="${p.title.replace(/"/g, '&quot;')}" loading="lazy" width="500" height="500">` : ''}</span>
        <span class="atc-fbt__title">${p.title}</span>
        <span class="atc-fbt__price">${sale ? `<s>${money(p.compare_at_price)}</s> ` : ''}<b>${money(p.price)}</b></span>
      </a>`;
  };

  const base = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  const getJSON = (url) => fetch(url, { credentials: 'same-origin' }).then((r) => (r.ok ? r.json() : null)).catch(() => null);

  Promise.all([
    getJSON(`${base}collections/all/products.json?limit=250`),
    getJSON(`${base}recommendations/products.json?product_id=${current.id}&limit=10&intent=related`),
  ]).then(([all, related]) => {
    if (!all || !all.products) return;
    const relatedIds = new Set(((related && related.products) || []).map((p) => p.id));
    const a = { id: current.id, type: current.type, tags: current.tags };

    // products.json has served tags both as an array and as one "a, b" string.
    all.products.forEach((p) => { if (typeof p.tags === 'string') p.tags = p.tags.split(/,\s*/); });
    const ranked = all.products
      .filter((p) => p.id !== current.id && !p.tags.includes('hidden-product') && p.product_type !== 'Service')
      .filter((p) => p.variants.some((v) => v.available))
      .map((p) => {
        const b = { id: p.id, type: p.product_type, tags: p.tags };
        return { p, b, s: score(a, b, relatedIds) };
      })
      .sort((x, y) => y.s - x.s);

    const perKind = {};
    const picked = [];
    for (const r of ranked) {
      if (picked.length >= LIMIT) break;
      const k = kind(r.b);
      if ((perKind[k] || 0) >= 2) continue;
      perKind[k] = (perKind[k] || 0) + 1;
      picked.push(r.p.handle);
    }
    if (!picked.length) return;

    // /products/<handle>.js gives prices in the shopper's own currency.
    return Promise.all(picked.map((h) => getJSON(`${base}products/${h}.js`))).then((items) => {
      const html = items.filter(Boolean).map(card).join('');
      if (!html) return;
      root.querySelector('.atc-fbt__row').innerHTML = html;
      // The row is rendered from layout/theme.liquid (so every product
      // template gets it). Move it to where Horizon's own "More items to
      // consider" sits and hide that one: it is Shopify's generic
      // related list, which repeats the same items less well.
      const wrap = root.closest('.shopify-section') || root;
      const rec = document.querySelector('product-recommendations');
      const recSection = rec && rec.closest('.shopify-section');
      if (recSection) {
        recSection.before(wrap);
        recSection.style.display = 'none';
      }
      root.hidden = false;
    });
  });
})();
